<script lang="ts" setup>
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  GpBtn,
  GpCard,
  GpExpandTransition,
  GpTextInput,
  GpTooltip,
  useFaIcons,
} from '@gportal/uikit';
import ReRegExp from 'reregexp';
import { computed, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import NodeNameGenerator from './NodeNameGenerator.vue';

const props = withDefaults(defineProps<{ limit?: number }>(), { limit: 10 });

const emit = defineEmits<{ (e: 'setNodes', value: Array<string>) }>();

useFaIcons({ faTrash });

const nodes = reactive(new Array<string>());

const filled = computed((): boolean => nodes.filter((x: string) => x === '').length === 0);

const deleteNode = () => {
  nodes.pop();
};

const root = ref<HTMLElement>();
const addNode = async () => {
  if (!filled.value || nodes.length >= props.limit) {
    return;
  }

  nodes.push('');

  await nextTick();
  const input = (root.value as HTMLDivElement).querySelector(
    `#text-input__fleet-config-node-${nodes.length - 1}`,
  ) as HTMLInputElement;
  if (input) {
    input.focus();
  }
};

const handleTab = (event: Event) => {
  if ((event as KeyboardEvent).key === 'Tab') {
    event.preventDefault();
    addNode();
  }
};

const fqdnInputs = ref<Array<HTMLInputElement>>([]);
const isGeneratorExpanded = ref(false);
const addGeneratedNodes = async (count: number, formular: string) => {
  const names = new ReRegExp(`/${formular}/`);
  nodes.length = 0;

  let iterations = count;
  nodes.forEach((c, i) => {
    if (!c) {
      nodes[i] = names.build();
      iterations -= 1;
    }
  });

  for (let i = 0; i < iterations; i += 1) {
    if (nodes.length >= props.limit) {
      return;
    }
    nodes.push(names.build());
  }

  await nextTick();
  if (fqdnInputs.value.length > 0) {
    fqdnInputs.value[0].focus();
  }
};

watch(
  () => nodes,
  () => {
    emit(
      'setNodes',
      nodes.filter((x: string) => x !== ''),
    );
  },
  { deep: true },
);

onMounted(() => {
  nodes.push('');
});

onUnmounted(() => {
  emit('setNodes', []);
});
</script>

<template>
  <div ref="root">
    <!--<GpBtn
      size="sm"
      label="Generate node names"
      @click="isGeneratorExpanded = !isGeneratorExpanded"
    >
      Generate node names
      <FontAwesomeIcon
        class="tw-ml-2 tw-transition-transform"
        :icon="['fas', 'fa-chevron-up']"
        :class="{
          'tw-rotate-180': isGeneratorExpanded,
          'tw-rotate-0': !isGeneratorExpanded,
        }"
      />
    </GpBtn>-->
    <GpExpandTransition>
      <NodeNameGenerator
        v-if="isGeneratorExpanded"
        class="tw-mt-4 tw-w-full md:tw-w-1/2"
        :limit="limit"
        @generate-nodes="addGeneratedNodes"
      />
    </GpExpandTransition>

    <div class="tw-mt-4 tw-w-full md:tw-w-1/2">
      <GpCard :enlarge-on-hover="false" :border-on-hover="false" class="tw-border-transparent">
        <div class="tw-p-4">
          <template v-for="(_, i) in nodes" :key="i">
            <GpTextInput
              ref="fqdnInputs"
              :class="{ 'tw-mt-4': i > 0 }"
              v-model="nodes[i]"
              :input-id="`fleet-config-node-${i}`"
              :label="`Enter a hostname for node ${i + 1}`"
              :name="`node${i}`"
              @keydown="handleTab"
            >
              <template #actions>
                <GpTooltip content="Delete node">
                  <button
                    v-if="i + 1 === nodes.length && nodes.length >= 2"
                    class="tw-p-0"
                    type="button"
                    @click="deleteNode"
                    style="height: 30px; width: 30px"
                    v-playwright="`nodes-create-fleet-remove-node-${i}`"
                  >
                    <FontAwesomeIcon class="tw-text-gp-fire" :icon="['fas', 'fa-trash']" />
                  </button>
                </GpTooltip>
              </template>
            </GpTextInput>
          </template>
        </div>
        <div class="tw-p-4">
          <GpBtn
            v-if="filled && nodes.length < limit"
            label="Add node"
            class="tw-w-full"
            outline
            :icon="['fas', 'plus']"
            @click="addNode"
            v-playwright="'nodes-create-fleet-add-node'"
          />
        </div>
      </GpCard>
    </div>
  </div>
</template>
